import { Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import { v2Route } from "~/v2/routes";
import { DataSourceTemplateDetail } from "./DataSourceTemplateDetail";

interface DataSourceTemplatePeekProps {
  id: string | null;
  onClose: () => void;
}

export function DataSourceTemplatePeek({ id, onClose }: DataSourceTemplatePeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = id !== null;

  const openFullPage = () => {
    if (!id) return;
    onClose();
    navigate(v2Route.dataSourceTemplates.detail.$buildPath({ params: { id } }));
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle className="capitalize">{t("model.dataSourceTemplate.modelName")}</SheetTitle>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage")}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label={t("ui.close")}>
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close")}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<div className="text-14-regular text-fg-3">…</div>}>
            {id !== null && <DataSourceTemplateDetail dataSourceTemplateId={id} onBack={onClose} />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
